import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import imageCompression from "browser-image-compression";
import { pdf } from "@react-pdf/renderer";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Skeleton } from "antd";
import { raffleCouponDetails } from "../../../controllers/V1/promocodes/promocodeController";
import { getPublicContent } from "../../../controllers/V1/raffleCoupon/raffleCouponContentController";
import { ASSETS_BASE_URL } from "../../../config/constants";
import BuyWinCouponPdf from "./BuyWinCouponPdf";
import { DEFAULT_COUPON_CONTENT, mergeCouponContent } from "./couponContentDefaults";

const resolveImageUrl = (path, assetBase) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.includes("upload")) {
  }
  return `${ASSETS_BASE_URL}${path.replace(/^\//, "")}`;
  // return `${assetBase}${path.startsWith("/") ? path : `/${path}`}`;
};

const COUPON_LAYOUT_WIDTH = 1050;
const PAGE_HORIZONTAL_PADDING = 32;

const pageStyle = {
  margin: 0,
  padding: "24px 16px",
  minHeight: "100vh",
  backgroundColor: "#d5d5d5",
  boxSizing: "border-box",
  fontFamily: "Arial, Helvetica, sans-serif",
  overflowX: "hidden",
};

const cardStyle = {
  width: `${COUPON_LAYOUT_WIDTH}px`,
  margin: "0 auto",
  display: "flex",
  flexShrink: 0,
  minHeight: "480px",
  boxShadow: "0 6px 28px rgba(0,0,0,0.3)",
  overflow: "hidden",
};

const ScaledCouponLayout = ({ children }) => {
  const contentRef = useRef(null);
  const [layoutScale, setLayoutScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const updateLayoutScale = () => {
      const availableWidth = window.innerWidth - PAGE_HORIZONTAL_PADDING;
      setLayoutScale(Math.min(1, availableWidth / COUPON_LAYOUT_WIDTH));
    };

    updateLayoutScale();
    window.addEventListener("resize", updateLayoutScale);
    return () => window.removeEventListener("resize", updateLayoutScale);
  }, []);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return undefined;

    const updateContentHeight = () => {
      setContentHeight(node.offsetHeight);
    };

    updateContentHeight();

    const observer = new ResizeObserver(updateContentHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div
      style={{
        width: COUPON_LAYOUT_WIDTH * layoutScale,
        height: contentHeight ? contentHeight * layoutScale : "auto",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <div
        ref={contentRef}
        style={{
          width: COUPON_LAYOUT_WIDTH,
          transform: `scale(${layoutScale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const CouponSkeleton = () => (
  <div style={pageStyle}>
    <ScaledCouponLayout>
    <div style={cardStyle}>
      <div
        style={{
          flex: "0 0 28%",
          backgroundColor: "#ffffff",
          borderRight: "2px dashed #888",
          padding: "18px 16px 14px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "26px",
            gap: "6px",
          }}
        >
          <Skeleton.Image active style={{ width: 75, height: 50 }} />
          <Skeleton.Button active size="small" style={{ width: 95, height: 32 }} />
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
          {[1, 2, 3, 4].map((item) => (
            <div key={item}>
              <Skeleton.Input active size="small" style={{ width: 72, marginBottom: 8, height: 16 }} />
              <Skeleton.Input active block style={{ height: 28 }} />
            </div>
          ))}
        </div>

        <Skeleton.Input active block style={{ height: 28, marginTop: 20 }} />
      </div>

      <div
        style={{
          flex: "1 1 72%",
          display: "flex",
          flexDirection: "column",
          minHeight: "480px",
          backgroundColor: "#1a0508",
        }}
      >
        <div style={{ padding: "14px 16px 0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "8px",
            }}
          >
            <Skeleton.Image active style={{ width: 80, height: 50 }} />
            <Skeleton.Button active size="small" style={{ width: 95, height: 32 }} />
          </div>
        </div>

        <div style={{ flex: 1, padding: "8px 16px" }}>
          <Skeleton.Image active style={{ width: "100%", minHeight: 220, display: "block" }} />
        </div>

        <div>
          <div
            style={{
              background: "linear-gradient(180deg, #c41e26 0%, #8f1419 100%)",
              borderTop: "3px solid #ffd700",
              padding: "16px 18px",
            }}
          >
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          </div>
          <div style={{ backgroundColor: "#0a0a0a", padding: "10px 16px" }}>
            <Skeleton.Input active block style={{ height: 14, backgroundColor: "rgba(255,255,255,0.08)" }} />
          </div>
        </div>
      </div>
    </div>
    </ScaledCouponLayout>
  </div>
);

const MIN_PDF_BYTES = 500 * 1024;
const MAX_PDF_BYTES = 1024 * 1024;

const PDF_IMAGE_PRESETS = {
  ultra: {
    raffle: {
      maxWidthOrHeight: 1800,
      maxSizeMB: 0.86,
      initialQuality: 0.97,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 400,
      maxSizeMB: 0.1,
      initialQuality: 0.98,
      fileType: "image/png",
    },
  },
  premium: {
    raffle: {
      maxWidthOrHeight: 1600,
      maxSizeMB: 0.78,
      initialQuality: 0.95,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 360,
      maxSizeMB: 0.09,
      initialQuality: 0.96,
      fileType: "image/png",
    },
  },
  high: {
    raffle: {
      maxWidthOrHeight: 1400,
      maxSizeMB: 0.68,
      initialQuality: 0.92,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 300,
      maxSizeMB: 0.08,
      initialQuality: 0.94,
      fileType: "image/png",
    },
  },
  standard: {
    raffle: {
      maxWidthOrHeight: 1200,
      maxSizeMB: 0.58,
      initialQuality: 0.88,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 240,
      maxSizeMB: 0.07,
      initialQuality: 0.9,
      fileType: "image/png",
    },
  },
  compact: {
    raffle: {
      maxWidthOrHeight: 1000,
      maxSizeMB: 0.5,
      initialQuality: 0.84,
      fileType: "image/jpeg",
    },
    logo: {
      maxWidthOrHeight: 200,
      maxSizeMB: 0.06,
      initialQuality: 0.88,
      fileType: "image/png",
    },
  },
};

const PDF_PRESET_ORDER = ["ultra", "premium", "high", "standard", "compact"];

const compressImageForPdf = async (url, options = {}) => {
  if (!url) return "";

  const {
    maxWidthOrHeight = 720,
    maxSizeMB = 0.72,
    initialQuality = 1,
    fileType = "image/jpeg",
  } = options;

  try {
    const response = await fetch(url);
    if (!response.ok) return url;
    const blob = await response.blob();
    const file = new File([blob], "image", { type: blob.type || fileType });
    const compressed = await imageCompression(file, {
      maxWidthOrHeight,
      maxSizeMB,
      initialQuality,
      useWebWorker: true,
      fileType,
      alwaysKeepResolution: false,
    });
    return await imageCompression.getDataUrlFromFile(compressed);
  } catch {
    return url;
  }
};

const loadPdfImages = async (assetBase, preset = PDF_IMAGE_PRESETS.premium, content = DEFAULT_COUPON_CONTENT) => {
  const rafflePath = resolveImageUrl(content.raffle_background_image, assetBase);
  const logoPath = resolveImageUrl(content.logo_image, assetBase);
  const [raffleImage, logoImage] = await Promise.all([
    compressImageForPdf(rafflePath, preset.raffle),
    compressImageForPdf(logoPath, preset.logo),
  ]);

  return {
    raffleBg: raffleImage,
    logoSrc: logoImage,
  };
};

const buildPdfBlob = async (couponPdfData, images) =>
  pdf(<BuyWinCouponPdf couponData={{ ...couponPdfData, ...images }} />).toBlob();

const selectOptimizedPdf = async (assetBase, couponPdfData, content = DEFAULT_COUPON_CONTENT) => {
  let bestUnderMax = null;

  for (const presetKey of PDF_PRESET_ORDER) {
    const images = await loadPdfImages(assetBase, PDF_IMAGE_PRESETS[presetKey], content);
    const blob = await buildPdfBlob(couponPdfData, images);

    if (blob.size > MAX_PDF_BYTES) continue;

    if (!bestUnderMax || blob.size > bestUnderMax.blob.size) {
      bestUnderMax = { images, blob };
    }

    if (blob.size >= MIN_PDF_BYTES) {
      return { images, blob };
    }
  }

  if (bestUnderMax) return bestUnderMax;

  const images = await loadPdfImages(assetBase, PDF_IMAGE_PRESETS.compact, content);
  return {
    images,
    blob: await buildPdfBlob(couponPdfData, images),
  };
};

const BuyWinCoupon = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const slNo = queryParams.get("sl_no") || "";
  const autoDownload = queryParams.get("autoDownload") || "";

  const [orderData, setOrderData] = useState(null);
  const [drawData, setDrawData] = useState(null);
  const [couponContent, setCouponContent] = useState(DEFAULT_COUPON_CONTENT);
  const [raffleBg, setRaffleBg] = useState("");
  const [logoSrc, setLogoSrc] = useState("");
  const [pdfImagesReady, setPdfImagesReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");
  const autoDownloadTriggered = useRef(false);

  useEffect(() => {
    document.title = couponContent.page_title || DEFAULT_COUPON_CONTENT.page_title;
  }, [couponContent.page_title]);

  useEffect(() => {
    const fetchCouponContent = async () => {
      const res = await getPublicContent();
      if (res.status === true && res.result) {
        setCouponContent(mergeCouponContent(res.result));
      }
    };

    fetchCouponContent();
  }, []);

  useEffect(() => {
    const fetchCouponDetails = async () => {
      if (!slNo) {
        setError("Invalid coupon code.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError("");
        const res = await raffleCouponDetails(slNo);
        if (res.status === true && res.result?.orderData) {
          setOrderData(res.result.orderData);
          setDrawData(res.result.drawData || null);
        } else {
          setError(res.message || "Coupon not found.");
        }
      } catch (err) {
        setError("Unable to load coupon. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCouponDetails();
  }, [slNo]);

  useEffect(() => {
    const preparePdfImages = async () => {
      const assetBase = `${window.location.origin}${process.env.PUBLIC_URL || ""}`;
      const images = await loadPdfImages(assetBase, PDF_IMAGE_PRESETS.premium, couponContent);
      setRaffleBg(images.raffleBg);
      setLogoSrc(images.logoSrc);
      setPdfImagesReady(Boolean(images.raffleBg && images.logoSrc));
    };

    preparePdfImages();
  }, [couponContent]);

  const name = [orderData?.buyer_first_name, orderData?.buyer_last_name]
    .filter(Boolean)
    .join(" ");
  const cpr = orderData?.cpr_number || "N/A";
  const order_no = orderData?.order_no || "N/A";
  const mob = orderData?.buyer_user_id?.country_code
    ? `${orderData.buyer_user_id.country_code} ${orderData.buyer_mobile || ""}`.trim()
    : orderData?.buyer_mobile || "";
  const couponCode = orderData?.coupon_code || slNo;
  const drawDateFormatted =
    orderData?.draw_date && orderData.draw_date !== "N/A"
      ? moment(`${orderData.draw_date} ${orderData.draw_time || "00:00"}:00`).format("DD-MM-YYYY hh:mm A")
      : "N/A";
  const promotionFrom = drawData?.from_date
    ? moment(drawData.from_date).format("DD-MM-YYYY")
    : orderData?.promocode_data?.valid_date
      ? moment(orderData.promocode_data.valid_date).format("DD-MM-YYYY")
      : "";
  const promotionTo = drawData?.to_date
    ? moment(drawData.to_date).format("DD-MM-YYYY")
    : orderData?.promocode_data?.expire_date
      ? moment(orderData.promocode_data.expire_date).format("DD-MM-YYYY")
      : "";
  const raffleCoupon = orderData?.coupon_code || "";
  const couponPdfData = useMemo(
    () => ({
      order_no,
      name,
      cpr,
      mob,
      couponCode,
      drawDateFormatted,
      promotionFrom,
      promotionTo,
      raffleCoupon,
      raffleBg,
      logoSrc,
      content: couponContent,
    }),
    [order_no, name, cpr, mob, couponCode, drawDateFormatted, promotionFrom, promotionTo, raffleCoupon, raffleBg, logoSrc, couponContent]
  );

  const pdfFileName = `Buy_Win_Raffle_Coupon_${couponCode}_${moment(new Date()).format("DD-MM-YYYY")}.pdf`;

  const fieldLine = (value) => ({
    borderBottom: "2px solid #333",
    minHeight: "28px",
    fontSize: "14px",
    color: "#111",
    paddingBottom: "2px",
    letterSpacing: value ? "normal" : "0.5px",
  });

  const assetBase = `${window.location.origin}${process.env.PUBLIC_URL || ""}`;
  const logoImageSrc = resolveImageUrl(couponContent.logo_image, assetBase);
  const raffleImageSrc = resolveImageUrl(couponContent.raffle_background_image, assetBase);

  const handleDownloadPdf = useCallback(async () => {
    if (!orderData) return;

    try {
      setIsDownloading(true);
      const assetBase = `${window.location.origin}${process.env.PUBLIC_URL || ""}`;
      const { blob } = await selectOptimizedPdf(assetBase, couponPdfData, couponContent);

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  }, [orderData, couponPdfData, pdfFileName, couponContent]);

  useEffect(() => {
    if (
      !isLoading &&
      orderData &&
      pdfImagesReady &&
      autoDownload === "Y" &&
      !autoDownloadTriggered.current
    ) {
      autoDownloadTriggered.current = true;
      handleDownloadPdf();
    }
  }, [isLoading, orderData, pdfImagesReady, autoDownload, handleDownloadPdf]);

  if (isLoading) {
    return <CouponSkeleton />;
  }

  if (error) {
    return (
      <div
        style={{
          margin: 0,
          padding: "24px 16px",
          minHeight: "100vh",
          backgroundColor: "#d5d5d5",
          boxSizing: "border-box",
          fontFamily: "Arial, Helvetica, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            padding: "24px 32px",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            textAlign: "center",
            maxWidth: "420px",
          }}
        >
          <p style={{ margin: 0, fontSize: "16px", color: "#a01820", fontWeight: "700" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div
        style={{
          width: COUPON_LAYOUT_WIDTH,
          maxWidth: "100%",
          margin: "0 auto 16px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadPdf}
          loading={isDownloading || !pdfImagesReady}
          disabled={!pdfImagesReady}
          style={{ backgroundColor: "#a01820", borderColor: "#a01820" }}
        >
          Download PDF
        </Button>
      </div>
      <ScaledCouponLayout>
      <div style={cardStyle}>
        {/* Left tear-off stub */}
        <div
          style={{
            flex: "0 0 28%",
            flexShrink: 0,
            backgroundColor: "#ffffff",
            borderRight: "2px dashed #888",
            padding: "18px 16px 14px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "26px",
              gap: "6px",
            }}
          >
            <img
              src={logoImageSrc}
              alt={couponContent.logo_alt}
              style={{ width: "75px", height: "auto", flexShrink: 0 }}
            />
            <div
              style={{
                border: "2px solid #222",
                borderRadius: "10px",
                padding: "5px 10px",
                fontSize: "12px",
                fontWeight: "700",
                color: "#222",
              }}
            >
              SL.NO : {order_no}
            </div>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Name :</div>
              <div style={fieldLine(name)}>{name}</div>
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>C.P.R :</div>
              <div style={fieldLine(cpr)}>{cpr}</div>
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Mob :</div>
              <div style={fieldLine(mob)}>{mob}</div>
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Raffle Coupon :</div>
              <div style={fieldLine(raffleCoupon)}>{raffleCoupon}</div>
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Draw Date :</div>
              <div style={fieldLine(drawDateFormatted)}>{drawDateFormatted}</div>
            </div>
          </div>

          <p
            style={{
              margin: "20px 0 0",
              fontSize: "10px",
              color: "#333",
              lineHeight: 1.35,
              textAlign: "center",
            }}
          >
            {couponContent.promo_text}
          </p>
        </div>

        {/* Right promotional body */}
        <div
          style={{
            flex: "1 1 72%",
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: "480px",
          }}
        >
          <img
            src={raffleImageSrc}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "65%",
              objectFit: "cover",
              objectPosition: "center 32%",
            }}
          />

          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: "inherit",
              padding: "14px 16px 0",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "8px",
                flexShrink: 0,
              }}
            >
              <img src={logoImageSrc} alt={couponContent.logo_alt} style={{ width: "80px" }} />
              <div
                style={{
                  border: "2px solid #fff",
                  borderRadius: "10px",
                  padding: "5px 12px",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.35)",
                }}
              >
                SL.NO : {order_no}
              </div>
            </div>

            <div style={{ flex: 1 }} />

            <div
              style={{
                flexShrink: 0,
                marginLeft: "-16px",
                marginRight: "-16px",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(180deg, #c41e26 0%, #8f1419 100%)",
                  borderTop: "3px solid #ffd700",
                  padding: "16px 18px",
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.1fr) auto minmax(0, 1.2fr)",
                  gap: "18px",
                  alignItems: "center",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#fff",
                    lineHeight: 1.5,
                    textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                  }}
                >
                  {couponContent.promo_text}
                </p>

                <div
                  style={{
                    textAlign: "center",
                    padding: "0 18px",
                    borderLeft: "1px solid rgba(255,215,0,0.35)",
                    borderRight: "1px solid rgba(255,215,0,0.35)",
                    alignSelf: "stretch",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "900",
                      color: "#ffd700",
                      letterSpacing: "1.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    {couponContent.brand_title}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      color: "#fff",
                      letterSpacing: "2px",
                      marginTop: "2px",
                    }}
                  >
                    {couponContent.brand_subtitle}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,215,0,0.2)",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    fontSize: "10px",
                    color: "#f5f5f5",
                    lineHeight: 1.5,
                  }}
                >
                  {drawDateFormatted !== "N/A" && (
                    <div
                      style={{
                        display: "inline-block",
                        backgroundColor: "#5c1010",
                        border: "1px solid rgba(255,215,0,0.45)",
                        borderRadius: "20px",
                        padding: "4px 12px",
                        marginBottom: "8px",
                        fontWeight: "700",
                        fontSize: "10px",
                        color: "#ffd700",
                        letterSpacing: "0.4px",
                      }}
                    >
                      DRAW: {drawDateFormatted}
                    </div>
                  )}
                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: "10px",
                      color: "#ffd700",
                      marginBottom: "5px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {couponContent.terms_title}
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "16px",
                      listStyleType: "disc",
                    }}
                  >
                    {(couponContent.terms_items || []).map((term, index) => (
                      <li key={index} style={{ marginBottom: "2px" }}>{term}</li>
                    ))}
                    {couponContent.show_promotion_dates === "Y" && promotionFrom && promotionTo && (
                      <li style={{ marginBottom: "2px" }}>
                        {couponContent.promotion_label}: {promotionFrom} to {promotionTo}
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#0a0a0a",
                  color: "#e8e8e8",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "6px 20px",
                  fontSize: "10px",
                  padding: "8px 16px",
                  letterSpacing: "0.2px",
                  borderTop: "1px solid rgba(255,215,0,0.15)",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      color: "#ffd700",
                      fontWeight: "700",
                      fontSize: "9px",
                      letterSpacing: "0.8px",
                    }}
                  >
                    MOB
                  </span>
                  {couponContent.contact_mobile}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      color: "#ffd700",
                      fontWeight: "700",
                      fontSize: "9px",
                      letterSpacing: "0.8px",
                    }}
                  >
                    LOCATION
                  </span>
                  {couponContent.contact_location}
                </span>
              </div>
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              right: "4px",
              top: "50%",
              transform: "translateY(-50%) rotate(90deg)",
              fontSize: "8px",
              color: "rgba(255,255,255,0.5)",
              whiteSpace: "nowrap",
              zIndex: 2,
            }}
          >
            {couponContent.side_note}
          </div>
        </div>
      </div>
      </ScaledCouponLayout>
    </div>
  );
};

export default BuyWinCoupon;
